import { useEffect, useRef } from "react";

const baseUrl =
	process.env.BUN_PUBLIC_SERVER_URL || "http://localhost:3000/api/v1";

interface UseOrderEventsOptions {
	onNewOrder?: (data: { orderId: number; status: string }) => void;
	enabled?: boolean;
}

/**
 * Hook to subscribe to real-time order events via SSE
 * Only works for admin users (endpoint requires admin auth)
 */
export function useOrderEvents({
	onNewOrder,
	enabled = true,
}: UseOrderEventsOptions = {}) {
	const eventSourceRef = useRef<EventSource | null>(null);
	const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);

	useEffect(() => {
		if (!enabled) return;

		const connect = () => {
			// Clean up existing connection
			if (eventSourceRef.current) {
				eventSourceRef.current.close();
			}

			const eventSource = new EventSource(`${baseUrl}/sse/orders`, {
				withCredentials: true,
			});

			eventSource.onopen = () => {
				console.log("[SSE] Connected to order events");
			};

			eventSource.addEventListener("new-order", (event) => {
				try {
					const data = JSON.parse(event.data);
					console.log("[SSE] New order received:", data);
					onNewOrder?.(data);
				} catch (error) {
					console.error("[SSE] Failed to parse event data:", error);
				}
			});

			eventSource.onerror = (error) => {
				console.error("[SSE] Connection error:", error);
				eventSource.close();

				// Reconnect after 5 seconds
				reconnectTimeoutRef.current = setTimeout(() => {
					console.log("[SSE] Attempting to reconnect...");
					connect();
				}, 5000);
			};

			eventSourceRef.current = eventSource;
		};

		connect();

		return () => {
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current);
			}
			if (eventSourceRef.current) {
				eventSourceRef.current.close();
				eventSourceRef.current = null;
			}
		};
	}, [enabled, onNewOrder]);

	return {
		isConnected: eventSourceRef.current?.readyState === EventSource.OPEN,
	};
}
